import React from 'react';
import {AlertTriangle, CheckCircle2, ChevronRight, Clock, Globe, XCircle} from 'lucide-react';

export default function SiteStatusWidget({ sites }) {
    // Regrouper par statut
    const sitesByStatus = {
        active: sites.filter(s => s.status === 'active'),
        warning: sites.filter(s => s.status === 'warning'),
        error: sites.filter(s => s.status === 'error'),
        inactive: sites.filter(s => s.status === 'inactive')
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'active': return CheckCircle2;
            case 'warning': return AlertTriangle;
            case 'error': return XCircle;
            default: return Clock;
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'active': return 'text-green-600 bg-green-50';
            case 'warning': return 'text-yellow-600 bg-yellow-50';
            case 'error': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const getStatusText = (status) => {
        switch(status) {
            case 'active': return 'Actif';
            case 'warning': return 'Avertissement';
            case 'error': return 'Erreur';
            default: return 'Inactif';
        }
    };

    return (
        <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Globe className="h-5 w-5 mr-2" />
                    Aperçu des Statuts
                </h3>
                <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                    Voir détails
                    <ChevronRight className="h-4 w-4 ml-1" />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(sitesByStatus).map(([status, siteList]) => {
                    const Icon = getStatusIcon(status);
                    const statusColor = getStatusColor(status);
                    const statusText = getStatusText(status);

                    // Calculer l'uptime moyen pour ce statut
                    const avgUptime = siteList.length > 0
                        ? siteList.reduce((sum, site) => sum + (site.uptime || 0), 0) / siteList.length
                        : 0;

                    return (
                        <div key={status} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-3">
                                <div className={`p-2 rounded-lg ${statusColor.split(' ')[1]}`}>
                                    <Icon className={`h-5 w-5 ${statusColor.split(' ')[0]}`} />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                  {siteList.length}
                </span>
                            </div>

                            <h4 className="font-medium text-gray-900 mb-1">{statusText}</h4>
                            <p className="text-sm text-gray-500 mb-3">
                                {siteList.length} site{siteList.length !== 1 ? 's' : ''}
                            </p>

                            {status === 'active' && avgUptime > 0 && (
                                <div className="mt-4 pt-3 border-t border-gray-100">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Uptime moyen</span>
                                        <span className="font-medium text-gray-900">{avgUptime.toFixed(1)}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${
                                                avgUptime >= 99 ? 'bg-green-500' :
                                                    avgUptime >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                                            }`}
                                            style={{ width: `${Math.min(avgUptime, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {siteList.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-xs text-gray-500 mb-2">Sites concernés:</p>
                                    <div className="space-y-2">
                                        {siteList.slice(0, 3).map(site => (
                                            <div key={site.id} className="flex items-center justify-between text-sm">
                                                <span className="truncate max-w-[70%]">{site.name}</span>
                                                <span className="text-gray-500">
                          {site.onlineVisitors || 0} en ligne
                        </span>
                                            </div>
                                        ))}
                                        {siteList.length > 3 && (
                                            <p className="text-xs text-gray-400 text-center">
                                                +{siteList.length - 3} autre{siteList.length - 3 > 1 ? 's' : ''}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
